// src/views/Admin/Home/Home.tsx
import React from 'react';
import { 
  HiOutlineBuildingOffice2,
  HiOutlineUsers,
  HiOutlineCurrencyDollar,
  HiOutlineClipboardDocumentList,
  HiOutlineExclamationTriangle,
  HiArrowTrendingUp,
  HiArrowTrendingDown
} from 'react-icons/hi2';
import './Home.scss';

// Mocked dashboard data
const dashboardStats = {
  activeLocales: 42,
  activeUsers: 156,
  monthlySales: 12500000,
  processedOrders: 8743,
  reportedErrors: 12,
  // Trend data (mock)
  trends: {
    locales: { value: 8, isPositive: true },
    users: { value: 12, isPositive: true },
    sales: { value: 15, isPositive: true },
    orders: { value: 23, isPositive: true },
    errors: { value: 3, isPositive: false }
  }
};

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, trend, color }) => {
  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <div className={`stat-icon-wrapper ${color}`}>
          <Icon className="stat-icon" size={24} />
        </div>
        {trend && (
          <div className={`stat-trend ${trend.isPositive ? 'positive' : 'negative'}`}>
            {trend.isPositive ? (
              <HiArrowTrendingUp size={16} />
            ) : (
              <HiArrowTrendingDown size={16} />
            )}
            <span>{trend.value}%</span>
          </div>
        )}
      </div>
      <div className="stat-card-body">
        <h3 className="stat-value">{value}</h3>
        <p className="stat-title">{title}</p>
      </div>
    </div>
  );
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('es-CL').format(value);
};

const AdminHome: React.FC = () => {
  return (
    <div className="admin-home">
      <div className="admin-home-header">
        <h1>Dashboard</h1>
        <p>Resumen general de MedioPunto</p>
      </div>

      <div className="stats-grid">
        <StatCard
          title="Locales Activos"
          value={formatNumber(dashboardStats.activeLocales)}
          icon={HiOutlineBuildingOffice2}
          trend={dashboardStats.trends.locales}
          color="orange"
        />
        <StatCard
          title="Usuarios Activos"
          value={formatNumber(dashboardStats.activeUsers)}
          icon={HiOutlineUsers}
          trend={dashboardStats.trends.users}
          color="blue"
        />
        <StatCard
          title="Ventas del Mes"
          value={formatCurrency(dashboardStats.monthlySales)}
          icon={HiOutlineCurrencyDollar}
          trend={dashboardStats.trends.sales}
          color="green"
        />
        <StatCard
          title="Pedidos Procesados"
          value={formatNumber(dashboardStats.processedOrders)}
          icon={HiOutlineClipboardDocumentList}
          trend={dashboardStats.trends.orders}
          color="purple"
        />
        <StatCard
          title="Errores Reportados"
          value={formatNumber(dashboardStats.reportedErrors)}
          icon={HiOutlineExclamationTriangle}
          trend={dashboardStats.trends.errors}
          color="red"
        />
      </div>

      <div className="recent-activity">
        <h2>Actividad Reciente</h2>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-dot green" />
            <div className="activity-content">
              <p className="activity-text">Nuevo local registrado: <strong>Restaurante El Sol</strong></p>
              <span className="activity-time">Hace 2 horas</span>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-dot blue" />
            <div className="activity-content">
              <p className="activity-text">Usuario agregado en <strong>Café Central</strong></p>
              <span className="activity-time">Hace 4 horas</span>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-dot orange" />
            <div className="activity-content">
              <p className="activity-text">Configuración actualizada en <strong>Bar La Luna</strong></p>
              <span className="activity-time">Hace 6 horas</span>
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-dot red" />
            <div className="activity-content">
              <p className="activity-text">Error reportado en <strong>Pizzería Napoli</strong></p>
              <span className="activity-time">Hace 8 horas</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;